import { emptySplitApi as api } from "./emptyApi";
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    newCaseCustomerOrder: build.mutation<
      NewCaseCustomerOrderApiResponse,
      NewCaseCustomerOrderApiArg
    >({
      query: (queryArg) => ({
        url: `/apiv2/customer-case-order`,
        method: "POST",
        body: queryArg.createCaseDto,
      }),
    }),
    priceCalculation: build.mutation<
      PriceCalculationApiResponse,
      PriceCalculationApiArg
    >({
      query: (queryArg) => ({
        url: `/apiv2/pricecalculation`,
        method: "POST",
        body: queryArg.newCaseV2WithAdditonalRequirements,
      }),
    }),
    getSelf: build.query<GetSelfApiResponse, GetSelfApiArg>({
      query: () => ({ url: `/customers/self` }),
    }),
    updateSelf: build.mutation<UpdateSelfApiResponse, UpdateSelfApiArg>({
      query: (queryArg) => ({
        url: `/customers/self`,
        method: "PUT",
        body: queryArg.updateRegisteredCustomerDto,
      }),
    }),
    login: build.mutation<LoginApiResponse, LoginApiArg>({
      query: (queryArg) => ({
        url: `/auth/login`,
        method: "POST",
        body: queryArg.usernamePasswordLoginRequest,
      }),
    }),
    logout: build.mutation<LogoutApiResponse, LogoutApiArg>({
      query: (queryArg) => ({
        url: `/auth/logout`,
        method: "POST",
        body: queryArg.body,
        cookies: {
          refresh_token: queryArg.refreshToken,
        },
      }),
    }),
    registerCustomer: build.mutation<
      RegisterCustomerApiResponse,
      RegisterCustomerApiArg
    >({
      query: (queryArg) => ({
        url: `/auth/register-customer`,
        method: "POST",
        body: queryArg.createRegisteredCustomerDto,
        headers: {
          "g-recaptcha-response": queryArg["g-recaptcha-response"],
        },
      }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as etApi };
export type NewCaseCustomerOrderApiResponse = unknown;
export type NewCaseCustomerOrderApiArg = {
  createCaseDto: CreateCaseDto;
};
export type PriceCalculationApiResponse =
  /** status 200 Price calculation result */ object[];
export type PriceCalculationApiArg = {
  newCaseV2WithAdditonalRequirements: NewCaseV2WithAdditonalRequirements;
};
export type GetSelfApiResponse =
  /** status 200 The customer */ RegisteredCustomer;
export type GetSelfApiArg = void;
export type UpdateSelfApiResponse =
  /** status 200 The customer */ RegisteredCustomer;
export type UpdateSelfApiArg = {
  updateRegisteredCustomerDto: UpdateRegisteredCustomerDto;
};
export type LoginApiResponse =
  /** status 200 The newly generated session and refresh tokens (if not requested as cookie!) */ LoginSuccess;
export type LoginApiArg = {
  usernamePasswordLoginRequest: UsernamePasswordLoginRequest;
};
export type LogoutApiResponse = unknown;
export type LogoutApiArg = {
  refreshToken?: string;
  body: string;
};
export type RegisterCustomerApiResponse = unknown;
export type RegisterCustomerApiArg = {
  "g-recaptcha-response"?: string;
  createRegisteredCustomerDto: CreateRegisteredCustomerDto;
};
export type General = {
  /** Unique, external id for the assignment */
  Number?: string;
  /** Unique, external id for the case. One case contains multiple assignments. One assignment can only be a child of exactly one case. */
  CaseNumber?: string;
  /** The ID of the order type (Auftragsart). Possible values:
    1 - Car opening
    2 - Wrong parking
    3 - Breakdown
    4 - Rental car
    5 - Ölspur
    8 - Pick-up
    9 - Sicherstellung
    10 - Others
    11 - Accident
    15 - Mobility
    23 - Intern order
    25 - National transport
    26 - International transport
    27 - Bicycle Assistance
    28 - Bicycle Assistance Breakdown
    29 - Bicycle Assistance Towing
    30 - Bicycle Assistance Salvage
    35 - Key emergency service
    41 - Transport Lite
    100 - Towing
    101 - Storno
    102 - Empty run
    105 - Second Towing
    106 - Bergen
    227 - Recycling
    505 - Angebot
    508 - Zentraler Noestang-Test
    513 - Einstellen */
  CaseType?: number;
  /** Id of the customers branch */
  Branch?: number;
  /** Short description of the case */
  Description?: string;
  /** Is the case preaccepted? true = No accept/refuse possibilities */
  IsPreaccepted?: boolean;
};
export type Location = {
  /** Name of the location */
  Name?: string;
  /** Description of the location */
  Description?: string;
  /** The (german) country name of the location */
  Country?: string;
  /** City of the location */
  City?: string;
  /** Street of the location */
  Street?: string;
  /** Zipcode of the location */
  Zipcode?: string;
  /** Landline number */
  Telephone?: string;
  /** Mobile number */
  Mobile?: string;
  /** Email */
  Email?: string;
  /** Name of person to contact */
  ContactPerson?: string;
  /** Date when to be at location in dd.MM.yyyy or dd.MM.yyyy HH:mm:ss format */
  Date?: string;
  /** Time in HH:mm:ss format */
  Time?: string;
  /** GPS Latitude in interval [-90,90]. Set to null for reverse geocoding. */
  Latitude?: number;
  /** GPS Longitude in interval [-180, 180]. Set to null for reverse geocoding. */
  Longitude?: number;
};
export type Vehicle = {
  /** The ID of the vehicle type. Possible values:
    1 - Motorcycle
    2 - Car
    3 - Car with trailer
    4 - Truck
    5 - Caravan
    6 - Trailer
    7 - Baggage
    8 - Bus
    9 - Scooter
    10 - Camper
    11 - Transporter
    12 - Car with roof box
    13 - SUV
    14 - Miscellaneous
    17 - Motorcycle
    30 - Bicycle
    31 - Cargo bike
    32 - Bike trailer
    33 - Boat
    34 - Minicar
    35 - Van */
  VehicleType?: number;
  /** Licenseplate number */
  LicensePlate?: string;
  /** Car brand and model */
  BrandModel?: string;
  /** Chassis number of the car */
  ChassisNumber?: string;
  /** The car's color */
  Color?: string;
  /** The car's weight in kilos */
  Weight?: number;
  /** Type of tries:
    "" | "Allwetter" | "Sommer" | "Winter"
     */
  TireType?: string;
  /** If the vehicle is officially registered. */
  Registered?: boolean;
};
export type CreateCaseDto = {
  General?: General;
  StartLocation?: Location;
  Destination?: Location;
  Vehicle?: Vehicle;
  /** Additional price list requirements */
  Requirements?: string[];
  /** Name of the invoice recipient */
  AlternateInvoiceRecipientName?: string;
  /** Address of the invoice recipient */
  ALternateInvoiceRecipientAddress?: string;
  /** Note left by the ordering customer */
  CustomerNote?: string;
  /** Whether the case is an inquiry or an actual order */
  IsInquiry?: boolean;
};
export type Customer = {
  /** Name of the customer or driver */
  Name?: string;
  /** Landline number */
  Telephone?: string;
  /** Mobile number */
  Mobile?: string;
};
export type NewCaseV2WithAdditonalRequirements = {
  General?: General;
  Customer?: Customer;
  StartLocation?: Location;
  Destination?: Location;
  Vehicle?: Vehicle;
  Requirements?: string[];
};
export type RegisteredCustomer = {
  /** Name of the customer or driver */
  Name?: string;
  /** Landline number */
  Telephone?: string;
  /** Mobile number */
  Mobile?: string;
  /** Email of the customer */
  Email?: string;
  /** City of customer */
  City?: string;
  /** Country of customer */
  Country?: number;
  /** Zipcode of customer */
  Zip?: string;
  /** Whole address of customer */
  Address?: string;
};
export type UpdateRegisteredCustomerDto = {
  /** Name of the customer or driver */
  Name?: string;
  /** Landline number */
  Telephone?: string;
  /** Mobile number */
  Mobile?: string;
  /** Email of the customer */
  Email?: string;
  /** City of customer */
  City?: string;
  /** Country of customer */
  Country?: number;
  /** Zipcode of customer */
  Zip?: string;
  /** Whole address of customer */
  Address?: string;
};
export type Token = {
  TokenData?: string;
  ExpiryUnixTimestamp?: number;
  CreatedAtUnitxTimestamp?: number;
  IsExpired?: boolean;
};
export type LoginSuccess = {
  SessionToken?: Token;
  RefreshToken?: Token;
};
export type UsernamePasswordLoginFailure = {
  UsernameOrPasswordIncorrect?: boolean;
  Message?: string;
};
export type UsernamePasswordLoginRequest = {
  SetTokensAsCookieInstead?: boolean;
  Username?: string;
  Password?: string;
};
export type CreateRegisteredCustomerDto = {
  /** Name of the customer or driver */
  Name?: string;
  /** Landline number */
  Telephone?: string;
  /** Mobile number */
  Mobile?: string;
  /** Email of the customer */
  Email?: string;
  /** City of customer */
  City?: string;
  /** Country of customer */
  Country?: number;
  /** Zipcode of customer */
  Zip?: string;
  /** Whole address of customer */
  Address?: string;
  /** Password to save for the new user */
  Password?: string;
};
export const {
  useNewCaseCustomerOrderMutation,
  usePriceCalculationMutation,
  useGetSelfQuery,
  useUpdateSelfMutation,
  useLoginMutation,
  useLogoutMutation,
  useRegisterCustomerMutation,
} = injectedRtkApi;
